// Template230: Kuala Lumpur - Cómo llegar desde el aeropuerto (Airports.jsx)
// Header, intro, box KLIA Ekspres, box Bus, box Grab/Taxi, yellow box KLIA vs KLIA2, stickers, box Consejo.

import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

const Template230 = ({
	backgroundColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page?.texts?.[0]?.content;
	const intro = page?.texts?.[1]?.content;
	const kliaTitle = page?.texts?.[2]?.content;
	const kliaList = page?.texts?.[3]?.content;
	const busTitle = page?.texts?.[4]?.content;
	const busList = page?.texts?.[5]?.content;
	const grabTitle = page?.texts?.[6]?.content;
	const grabList = page?.texts?.[7]?.content;
	const kliaTip = page?.texts?.[8]?.content;
	const consejoTitle = page?.texts?.[9]?.content;
	const consejoBody = page?.texts?.[10]?.content;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found?.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found?.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const stickerImage = getImageSrc("sticker");
	const sticker2Image = getImageSrc("sticker2");
	const logoImage = getCanvasImageUrl("logo");
	const actualHeaderColor = headerBackgroundColor || "#2E7D5B";

	const boxStyle = {
		backgroundColor: "#dee7e3",
		border: "2px solid #2E7D5B",
		borderRadius: "12px",
		padding: "16px",
	};

	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "8px",
		marginTop: 0,
		color: "#2e7d5b",
	};

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: "#444",
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
				{logoImage && !isSmallScreen && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{intro && (
				<Box data-block-id="text-1" sx={{ padding: "12px 16px", fontSize: 13, color: "#444" }}>
					<Box component="p" sx={estiloP}>
						{intro}
					</Box>
				</Box>
			)}

			<Box
				data-block-id="text-2"
				sx={{
					padding: "0 16px 20px",
					display: "flex",
					flexDirection: "column",
					gap: 2,
					position: "relative",
				}}
			>
				{/* KLIA Ekspres */}
				{(kliaTitle || kliaList) && (
					<Box data-block-id="text-3" sx={boxStyle}>
						{kliaTitle && (
							<Box component="h3" sx={estiloH3}>
								{kliaTitle}
							</Box>
						)}
						{kliaList && (
							<Box
								component="ul"
								sx={{ paddingLeft: "18px", margin: 0, fontSize: "13px" }}
								dangerouslySetInnerHTML={{ __html: kliaList }}
							/>
						)}
					</Box>
				)}

				{/* Bus */}
				{(busTitle || busList) && (
					<Box data-block-id="text-4" sx={boxStyle}>
						{busTitle && (
							<Box component="h3" sx={estiloH3}>
								{busTitle}
							</Box>
						)}
						{busList && (
							<Box
								component="ul"
								sx={{ paddingLeft: "18px", margin: 0, fontSize: "13px" }}
								dangerouslySetInnerHTML={{ __html: busList }}
							/>
						)}
					</Box>
				)}

				{/* Grab / Taxi */}
				{(grabTitle || grabList) && (
					<Box data-block-id="text-5" sx={boxStyle}>
						{grabTitle && (
							<Box component="h3" sx={estiloH3}>
								{grabTitle}
							</Box>
						)}
						{grabList && (
							<Box
								component="ul"
								sx={{ paddingLeft: "18px", margin: 0, fontSize: "13px" }}
								dangerouslySetInnerHTML={{ __html: grabList }}
							/>
						)}
					</Box>
				)}

				{kliaTip && (
					<Box
						data-block-id="text-6"
						sx={{
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "12px",
							padding: "12px 14px",
							fontSize: 13,
							marginTop: "10px",
						}}
						dangerouslySetInnerHTML={{ __html: kliaTip }}
					/>
				)}

				{stickerImage && (
					<Box
						component="img"
						src={stickerImage}
						alt=""
						sx={{
							width: isSmallScreen ? 90 : 100,
							position: "absolute",
							top: isSmallScreen ? -40 : 40,
							right: -10,
						}}
					/>
				)}
				{sticker2Image && (
					<Box
						component="img"
						src={sticker2Image}
						alt=""
						sx={{
							width: 100,
							position: "absolute",
							bottom: isSmallScreen ? -40 : 70,
							right: -10,
						}}
					/>
				)}

				{(consejoTitle || consejoBody) && (
					<Box
						data-block-id="text-7"
						sx={{
							backgroundColor: "#dee7e3",
							border: "2px solid #2E7D5B",
							borderRadius: "12px",
							padding: "16px",
							fontSize: 12,
							color: "#444",
						}}
					>
						{consejoTitle && (
							<Box
								component="h4"
								sx={{
									marginTop: 0,
									marginBottom: "8px",
									fontSize: 13,
									color: "#2e7d5b",
									fontWeight: 700,
									textTransform: "uppercase",
								}}
							>
								{consejoTitle}
							</Box>
						)}
						{consejoBody && (
							<Box component="p" sx={estiloP}>
								{consejoBody}
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template230.thumbnail = "/images/template-1.png";
export default Template230;
